import { HttpClient } from '@angular/common/http';
import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';

import { SecretariesSettingsService } from 'app/core/http/services/secretaries-settings.service';
import { SecretarySettings } from 'app/core/interfaces/secretaries-settings';
import { buildRecipientVisualStatus, RecipientVisualStatus } from 'app/core/utils/recipient-status';
import { cnpjValidator } from 'app/core/validators/CnpjValidator';
import { cpfValidator } from 'app/core/validators/CpfValidator';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { OnboardingPagarmeComponent } from 'app/modules/shared/components/onboarding-pagarme/onboarding-pagarme.component';

import { DialogService } from 'app/services/dialog.service';

interface BankOption {
  ispb: string;
  name: string;
  code: number;
  fullName: string;
}

@Component({
  selector: 'secretary-bank-account',
  templateUrl: './secretary-bank-account.component.html',
  styleUrls: ['./secretary-bank-account.component.scss'],
})
export class SecretaryBankAccountComponent extends BaseComponent implements OnInit, OnDestroy {
  public bankDetailsForm: FormGroup;

  public mode = 'CREATE';
  public submittedSecretariesSettings = false;
  public successSecretarySettings = false;

  public secretarySettings!: SecretarySettings;

  public mask: string | null = null;

  public documentMask: string | null = null;
  public documentPlaceholder = 'Selecione o tipo de pessoa';

  public banks: BankOption[] = [];
  public filteredBanks$!: Observable<BankOption[]>;

  public recipient_id: string = '';

  public transferDays: Array<{
    value: number;
    label: string;
  }> = [];

  showTransferDay = false;
  transferDayLabel = 'Dia de Recebimento';
  transferDayOptionPrefix = 'Dia';
  transferDayErrorMessage = 'Informe um dia válido.';

  private readonly banksApiUrl = 'https://brasilapi.com.br/api/banks/v1';
  private readonly settingsRefreshDelayMs = 5000;
  private settingsRefreshTimer?: ReturnType<typeof setTimeout>;
  private destroyed = false;
  private onboardingRequestInFlight = false;
  private onboardingLoadingDialogRef?: MatDialogRef<OnboardingPagarmeComponent>;
  private onboardingDialogRef?: MatDialogRef<OnboardingPagarmeComponent>;

  constructor(
    private fb: FormBuilder,
    private secretarySettingsService: SecretariesSettingsService,
    private injector: Injector,
    private http: HttpClient,
    private dialogService: DialogService,
  ) {
    super(injector);

    this.bankDetailsForm = this.fb.group({
      bank_search: [null, Validators.required],

      holder_name: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(30), Validators.pattern(/^(?=.*\S).+$/)]],
      holder_type: [null, Validators.required],
      holder_document: [null, Validators.required],

      bank: [null, [Validators.required, Validators.pattern(/^\d{3}$/)]],
      bank_number: [null, [Validators.required, Validators.pattern(/^\d{3}$/)]],

      branch_number: [null, [Validators.required, Validators.pattern(/^\d{1,5}$/)]],
      branch_check_digit: ['', [Validators.pattern(/^\d?$/)]],

      account_number: ['', [Validators.required, Validators.pattern(/^\d{4,13}$/)]],
      account_check_digit: ['', [Validators.required, Validators.pattern(/^[0-9Xx]$/)]],

      type: [null, Validators.required],

      transfer_interval: ['daily', Validators.required],
      transfer_day: [null, [Validators.required, Validators.min(1), Validators.max(31)]],
    });

    this.configureBankFilter();
    this.watchDocumentType();
  }

  ngOnInit(): void {
    this.onTransferIntervalChanged();

    this.bankDetailsForm.get('transfer_interval')?.valueChanges.subscribe(() => {
      this.onTransferIntervalChanged();
    });

    this.loadBanks();
    this.getSetting(() => this.refreshSettingsAfterSave());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.closeOnboardingLoadingModal();
    this.onboardingDialogRef?.close();

    if (this.settingsRefreshTimer) {
      clearTimeout(this.settingsRefreshTimer);
    }
  }

  get bankSearch() {
    return this.bankDetailsForm.get('bank_search');
  }

  get accountNumber() {
    return this.bankDetailsForm.get('account_number');
  }

  get accountDigit() {
    return this.bankDetailsForm.get('account_check_digit');
  }

  get documentControl() {
    return this.bankDetailsForm.get('holder_document');
  }

  get holderName() {
    return this.bankDetailsForm.get('holder_name');
  }

  get currentRecipientId(): string {
    return this.getRecipientId(this.secretarySettings) || this.recipient_id || '';
  }

  get canOpenOnboarding(): boolean {
    return !!this.currentRecipientId && this.recipientStatus.code !== 'active' && (this.hasValidOnboardingLink() || this.canCreateOnboardingLink());
  }

  get recipientStatus(): RecipientVisualStatus {
    return buildRecipientVisualStatus(this.secretarySettings);
  }

  private configureBankFilter(): void {
    this.filteredBanks$ = this.bankDetailsForm.get('bank_search')!.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterBanks(value)),
    );
  }

  onTransferIntervalChanged(): void {
    const interval = this.bankDetailsForm.get('transfer_interval')?.value;
    const transferDayControl = this.bankDetailsForm.get('transfer_day');

    if (!transferDayControl) {
      return;
    }

    if (interval === 'daily') {
      this.showTransferDay = false;
      this.transferDays = [];

      transferDayControl.setValue(0);
      transferDayControl.clearValidators();
      transferDayControl.updateValueAndValidity();

      return;
    }

    if (interval === 'weekly') {
      this.showTransferDay = true;

      this.transferDayLabel = 'Dia da Semana para Recebimento';
      this.transferDayErrorMessage = 'Selecione um dia da semana.';

      this.transferDays = [
        { value: 1, label: 'Segunda-feira' },
        { value: 2, label: 'Terça-feira' },
        { value: 3, label: 'Quarta-feira' },
        { value: 4, label: 'Quinta-feira' },
        { value: 5, label: 'Sexta-feira' },
      ];

      transferDayControl.setValidators([Validators.required, Validators.min(1), Validators.max(5)]);

      const validDays = this.transferDays.map((day) => day.value);

      if (!validDays.includes(Number(transferDayControl.value))) {
        transferDayControl.setValue(1);
      }

      transferDayControl.updateValueAndValidity();

      return;
    }

    if (interval === 'monthly') {
      this.showTransferDay = true;

      this.transferDayLabel = 'Dia do Mês para Recebimento';
      this.transferDayErrorMessage = 'Informe um dia entre 1 e 31.';

      this.transferDays = Array.from({ length: 31 }, (_, index) => ({
        value: index + 1,
        label: `Dia ${index + 1}`,
      }));

      transferDayControl.setValidators([Validators.required, Validators.min(1), Validators.max(31)]);

      const validDays = this.transferDays.map((day) => day.value);

      if (!validDays.includes(Number(transferDayControl.value))) {
        transferDayControl.setValue(1);
      }

      transferDayControl.updateValueAndValidity();

      return;
    }
  }

  private loadBanks(): void {
    this.http
      .get<BankOption[]>(this.banksApiUrl)
      .pipe(
        map((banks) => {
          return banks
            .filter((bank) => bank.code !== null && bank.code !== undefined)
            .sort((a, b) => {
              const nameA = a.name || a.fullName || '';
              const nameB = b.name || b.fullName || '';

              return nameA.localeCompare(nameB);
            });
        }),
        catchError((err) => {
          console.error('Erro ao carregar bancos:', err);
          this.showErrorMessage = true;
          return of([]);
        }),
      )
      .subscribe((banks) => {
        this.banks = banks;

        const currentBankCode = this.bankDetailsForm.get('bank_number')?.value || this.bankDetailsForm.get('bank')?.value;

        if (currentBankCode) {
          this.setBankSearchByCode(currentBankCode);
        }

        this.bankDetailsForm.get('bank_search')?.updateValueAndValidity();
      });
  }

  private watchDocumentType(): void {
    this.bankDetailsForm.get('holder_type')?.valueChanges.subscribe((holderType) => {
      const control = this.bankDetailsForm.get('holder_document');

      control?.clearValidators();
      this.documentMask = null;
      this.documentPlaceholder = 'Selecione o tipo de pessoa';

      if (holderType === 'individual') {
        control?.setValidators([Validators.required, cpfValidator]);
        this.documentMask = '000.000.000-00';
        this.documentPlaceholder = 'Digite o CPF';
      } else if (holderType === 'company') {
        control?.setValidators([Validators.required, cnpjValidator]);
        this.documentMask = '00.000.000/0000-00';
        this.documentPlaceholder = 'Digite o CNPJ';
      } else {
        control?.setValidators([Validators.required]);
      }

      control?.updateValueAndValidity();
    });
  }

  public displayBank = (bank: BankOption | string | null): string => {
    if (!bank) {
      return '';
    }

    if (typeof bank === 'string') {
      return bank;
    }

    return this.getBankLabel(bank);
  };

  public onBankSelected(event: MatAutocompleteSelectedEvent): void {
    const bank = event.option.value as BankOption;
    this.selectBank(bank);
  }

  public onBankInputChanged(): void {
    this.bankDetailsForm.patchValue(
      {
        bank: null,
        bank_number: null,
      },
      { emitEvent: false },
    );

    this.bankDetailsForm.get('bank')?.markAsTouched();
    this.bankDetailsForm.get('bank_number')?.markAsTouched();

    this.bankDetailsForm.get('bank')?.updateValueAndValidity();
    this.bankDetailsForm.get('bank_number')?.updateValueAndValidity();
  }

  public trySelectTypedBank(): void {
    setTimeout(() => {
      const value = this.bankDetailsForm.get('bank_search')?.value;

      if (!value || typeof value !== 'string') {
        return;
      }

      const bank = this.findExactBank(value);

      if (bank) {
        this.selectBank(bank);
      }
    }, 200);
  }

  private selectBank(bank: BankOption): void {
    const bankCode = this.formatBankCode(bank.code);

    this.bankDetailsForm.patchValue({
      bank_search: bank,
      bank: bankCode,
      bank_number: bankCode,
    });

    this.bankDetailsForm.get('bank_search')?.markAsTouched();
    this.bankDetailsForm.get('bank')?.markAsTouched();
    this.bankDetailsForm.get('bank_number')?.markAsTouched();

    this.bankDetailsForm.get('bank')?.updateValueAndValidity();
    this.bankDetailsForm.get('bank_number')?.updateValueAndValidity();
  }

  private setBankSearchByCode(code: string | number): void {
    const bank = this.findBankByCode(code);
    const bankCode = this.formatBankCode(code);

    if (bank) {
      this.bankDetailsForm.patchValue(
        {
          bank_search: bank,
          bank: this.formatBankCode(bank.code),
          bank_number: this.formatBankCode(bank.code),
        },
        { emitEvent: false },
      );
    } else {
      this.bankDetailsForm.patchValue(
        {
          bank_search: bankCode,
          bank: bankCode,
          bank_number: bankCode,
        },
        { emitEvent: false },
      );
    }
  }

  private filterBanks(value: BankOption | string | null): BankOption[] {
    const search = typeof value === 'string' ? value : value ? this.getBankLabel(value) : '';

    const term = this.normalizeText(search);
    const digits = search.replace(/\D/g, '');

    if (!term && !digits) {
      return this.banks.slice(0, 30);
    }

    return this.banks
      .filter((bank) => {
        const code = this.formatBankCode(bank.code);

        const bankText = this.normalizeText(`
          ${bank.name || ''}
          ${bank.fullName || ''}
          ${code}
        `);

        const matchByName = !!term && bankText.includes(term);
        const matchByCode = !!digits && code.includes(digits);

        return matchByName || matchByCode;
      })
      .slice(0, 50);
  }

  private findExactBank(value: string): BankOption | undefined {
    const digits = value.replace(/\D/g, '');
    const term = this.normalizeText(value);

    if (digits) {
      const bankByCode = this.findBankByCode(digits);

      if (bankByCode) {
        return bankByCode;
      }
    }

    return this.banks.find((bank) => {
      const name = this.normalizeText(bank.name || '');
      const fullName = this.normalizeText(bank.fullName || '');
      const label = this.normalizeText(this.getBankLabel(bank));

      return name === term || fullName === term || label === term;
    });
  }

  private findBankByCode(code: string | number): BankOption | undefined {
    const normalizedCode = this.normalizeBankCode(code);

    return this.banks.find((bank) => {
      return this.normalizeBankCode(bank.code) === normalizedCode;
    });
  }

  public formatBankCode(code: string | number | null | undefined): string {
    return String(code ?? '')
      .replace(/\D/g, '')
      .padStart(3, '0');
  }

  private normalizeBankCode(code: string | number | null | undefined): string {
    const onlyDigits = String(code ?? '').replace(/\D/g, '');
    const withoutLeadingZeros = onlyDigits.replace(/^0+/, '');

    return withoutLeadingZeros || '0';
  }

  private getBankLabel(bank: BankOption): string {
    return `${this.formatBankCode(bank.code)} - ${bank.fullName || bank.name}`;
  }

  private normalizeText(value: string): string {
    return String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private onlyDigits(value: string | number | null | undefined): string {
    return String(value ?? '').replace(/\D/g, '');
  }

  private normalizeAccountDigit(value: string | null | undefined): string {
    return String(value ?? '')
      .trim()
      .toUpperCase();
  }

  private normalizeBranchCheckDigit(value: string | number | null | undefined): string {
    const normalized = String(value ?? '').trim();
    return normalized || '0';
  }

  public onBranchCheckDigitBlur(): void {
    const control = this.bankDetailsForm.get('branch_check_digit');

    if (!control) {
      return;
    }

    if (control.value === null || control.value === undefined || String(control.value).trim() === '') {
      control.setValue('0');
    }
  }

  private splitAccountNumber(accountNumber?: string | null): { number: string | null; digit: string | null } {
    if (!accountNumber) {
      return {
        number: null,
        digit: null,
      };
    }

    const [number, digit] = accountNumber.split('-');

    return {
      number: number || null,
      digit: digit || null,
    };
  }

  private getBankAccountPayload(): NonNullable<SecretarySettings['bank_account']> {
    const raw = this.bankDetailsForm.getRawValue();

    const bankCode = this.formatBankCode(raw.bank_number || raw.bank);

    const payload: NonNullable<SecretarySettings['bank_account']> = {
      holder_name: String(raw.holder_name).trim(),
      holder_type: raw.holder_type,
      holder_document: this.onlyDigits(raw.holder_document),

      bank: bankCode,
      bank_number: bankCode,

      branch_number: this.onlyDigits(raw.branch_number),
      branch_check_digit: this.normalizeBranchCheckDigit(raw.branch_check_digit),

      account_number: this.onlyDigits(raw.account_number),
      account_check_digit: this.normalizeAccountDigit(raw.account_check_digit),

      type: raw.type,
    };

    return payload;
  }

  private getTransferSettingsPayload(): NonNullable<SecretarySettings['transfer_settings']> {
    const raw = this.bankDetailsForm.getRawValue();

    return {
      transfer_interval: raw.transfer_interval || 'daily',
      transfer_day: String(raw.transfer_day || '0'),
    };
  }

  public getAllFormErrors(): Record<string, any> {
    const allErrors: Record<string, any> = {};

    for (const [key, control] of Object.entries(this.bankDetailsForm.controls)) {
      if (control.errors) {
        allErrors[key] = control.errors;
      }
    }

    return allErrors;
  }

  public getSetting(afterLoad?: () => void): void {
    this.secretarySettingsService.findBySecretary(this.getUserLogged().id).subscribe({
      next: (secretarySettings: SecretarySettings) => {
        this.setSecretarySettings(secretarySettings);
        this.mode = 'UPDATE';

        const bankAccount = this.secretarySettings.bank_account;
        const transferSettings = this.secretarySettings.transfer_settings;

        const bankCode = bankAccount?.bank_number || bankAccount?.bank;
        const accountParts = this.splitAccountNumber(bankAccount?.account_number);

        this.bankDetailsForm.patchValue({
          holder_name: bankAccount?.holder_name,
          holder_type: bankAccount?.holder_type,
          holder_document: bankAccount?.holder_document,

          bank: bankCode ? this.formatBankCode(bankCode) : null,
          bank_number: bankCode ? this.formatBankCode(bankCode) : null,

          branch_number: bankAccount?.branch_number,
          branch_check_digit: bankAccount?.branch_check_digit,

          account_number: accountParts.digit ? accountParts.number : bankAccount?.account_number,
          account_check_digit: bankAccount?.account_check_digit || accountParts.digit,

          type: bankAccount?.type,

          transfer_interval: transferSettings?.transfer_interval || 'daily',
          transfer_day: transferSettings?.transfer_day ?? 1,
        });

        if (bankCode) {
          this.setBankSearchByCode(bankCode);
        }

        if (!this.currentRecipientId) {
          afterLoad?.();
          return;
        }

        afterLoad?.();
      },
      error: (err: any) => {
        console.error(err);
        afterLoad?.();
      },
    });
  }

  public openOrCreateOnboarding(): void {
    const recipientId = this.currentRecipientId;

    if (!recipientId) {
      this.messageError = 'Salve os dados bancarios antes de abrir o cadastro Pagar.me.';
      this.showErrorMessage = true;
      return;
    }

    if (this.recipientStatus.code === 'active') {
      return;
    }

    if (this.hasValidOnboardingLink()) {
      this.openOnboardingModal(this.secretarySettings.onboard_qrcode);
      return;
    }

    if (!this.canCreateOnboardingLink()) {
      return;
    }

    this.createOnboarding(this.secretarySettings.secretary_id || this.getUserLogged().id, recipientId, this.secretarySettings.secretary_settings_id);
  }

  private isExpired(expiresAt: string): boolean {
    if (!expiresAt) {
      return true;
    }

    const expirationDate = new Date(expiresAt);

    if (Number.isNaN(expirationDate.getTime())) {
      return true;
    }

    return Date.now() >= expirationDate.getTime();
  }

  private canCreateOnboardingLink(): boolean {
    const recipientInfo = this.secretarySettings?.recipient_info || {};
    const kycDetails = recipientInfo.kyc_details || {};

    return (
      String(recipientInfo.status || '').toLowerCase() === 'affiliation' &&
      String(kycDetails.status || '').toLowerCase() === 'partially_denied' &&
      String(kycDetails.status_reason || '').toLowerCase() === 'additional_documents_required'
    );
  }

  get onboardingButtonLabel(): string {
    if (!this.currentRecipientId || this.canOpenOnboarding) {
      return 'Abrir cadastro Pagar.me';
    }

    return 'Aguardando liberacao da Pagar.me';
  }

  private hasValidOnboardingLink(): boolean {
    const onboarding = this.secretarySettings?.onboard_qrcode;
    return !!onboarding?.url && !!onboarding.expires_at && !this.isExpired(onboarding.expires_at);
  }

  openOnboardingModal(recipient_onboard: any) {
    this.onboardingDialogRef = this.dialogService.openMediumComponentDialogRef(OnboardingPagarmeComponent, {
      param: recipient_onboard,
    });
    this.onboardingDialogRef.afterClosed().subscribe(() => {
      this.onboardingDialogRef = undefined;
    });
  }

  public createOrUpdateSettings(): void {
    if (this.bankDetailsForm.invalid) {
      this.bankDetailsForm.markAllAsTouched();
      return;
    }

    if (this.mode === 'CREATE') {
      this.createSettings();
      return;
    }

    this.updateSettings();
  }

  private createSettings(): void {
    const newSecretarySettings: SecretarySettings = {
      secretary_id: this.getUserLogged().id,
      name: this.getUserLogged().name,
      email: this.getUserLogged().email,
      bank_account: this.getBankAccountPayload(),
      transfer_settings: this.getTransferSettingsPayload(),
    };
    this.submittedSecretariesSettings = true;
    this.loading = true;

    this.secretarySettingsService.create(newSecretarySettings).subscribe({
      next: (secretarySettings: SecretarySettings) => {
        this.showSuccessMessage = true;
        this.setSecretarySettings(secretarySettings);
        this.mode = 'UPDATE';
        this.loading = false;
        this.startOnboardingFlow();
        this.refreshSettingsAfterSave();
      },
      error: (err: any) => {
        this.showErrorMessage = true;
        this.loading = false;
        console.error(err);
      },
    });
  }

  private createOnboarding(secretary_id: string, recipient_id: string, secretary_settings_id?: string): void {
    if (this.onboardingRequestInFlight) {
      return;
    }

    this.onboardingRequestInFlight = true;
    this.submittedSecretariesSettings = true;
    this.loading = true;
    this.openOnboardingLoadingModal();

    this.secretarySettingsService.createOnboarding(secretary_id, recipient_id, secretary_settings_id).subscribe({
      next: (onboarding: any) => {
        this.secretarySettings = {
          ...this.secretarySettings,
          onboard_qrcode: onboarding,
        };
        this.onboardingRequestInFlight = false;
        this.showSuccessMessage = true;
        this.loading = false;
        this.closeOnboardingLoadingModal();
        this.openOnboardingModal(onboarding);
      },
      error: (err: any) => {
        this.onboardingRequestInFlight = false;
        this.closeOnboardingLoadingModal();
        this.messageError = this.getRequestErrorMessage(err);
        this.showErrorMessage = true;
        this.loading = false;
        console.error(err);
      },
    });
  }

  private startOnboardingFlow(): void {
    const recipientId = this.currentRecipientId;

    if (!recipientId || this.recipientStatus.code === 'active') {
      return;
    }

    if (this.hasValidOnboardingLink()) {
      this.openOnboardingModal(this.secretarySettings?.onboard_qrcode);
      return;
    }

    this.openOnboardingLoadingModal();
    if (this.canCreateOnboardingLink()) {
      this.createOnboarding(
        this.secretarySettings.secretary_id || this.getUserLogged().id,
        recipientId,
        this.secretarySettings.secretary_settings_id,
      );
    }
  }

  private openOnboardingLoadingModal(): void {
    if (!this.onboardingLoadingDialogRef) {
      this.onboardingLoadingDialogRef = this.dialogService.openMediumComponentDialogRef(OnboardingPagarmeComponent, { loading: true });
    }
  }

  private closeOnboardingLoadingModal(): void {
    this.onboardingLoadingDialogRef?.close();
    this.onboardingLoadingDialogRef = undefined;
  }

  private openOnboardingWhenAvailable(): void {
    if (this.onboardingLoadingDialogRef && this.hasValidOnboardingLink()) {
      const onboarding = this.secretarySettings?.onboard_qrcode;
      this.closeOnboardingLoadingModal();
      this.openOnboardingModal(onboarding);
    }
  }

  private handleOnboardingStatus(): void {
    const recipient = this.secretarySettings?.recipient_info || {};
    const kycDetails = recipient.kyc_details || {};
    const kycStatus = String(kycDetails.status || recipient.kyc_status || '')
      .trim()
      .toLowerCase();
    const kycStatusReason = String(kycDetails.status_reason || '')
      .trim()
      .toLowerCase();
    const submitted =
      kycStatus === 'pending' && ['in_analysis', 'answered_waiting_analysis', 'waiting_manual_risk_analysis'].includes(kycStatusReason);
    const active = this.recipientStatus.code === 'active';

    if ((submitted || active) && (this.onboardingDialogRef || this.onboardingLoadingDialogRef)) {
      this.onboardingDialogRef?.close();
      this.closeOnboardingLoadingModal();
      this.messageSuccess = active
        ? 'Dados enviados com sucesso. O cadastro foi aprovado pela Pagar.me e está ativo.'
        : 'Dados enviados com sucesso. O cadastro está em análise pela Pagar.me.';
      this.showSuccessMessage = true;
    }
  }

  private getRequestErrorMessage(error: any): string {
    const responseMessage = error?.error?.message;

    if (typeof responseMessage === 'string') {
      return responseMessage;
    }

    if (typeof responseMessage?.message === 'string') {
      return responseMessage.message;
    }

    if (Array.isArray(responseMessage?.errors) && responseMessage.errors.length > 0) {
      const firstError = responseMessage.errors[0];

      if (typeof firstError?.message === 'string') {
        return firstError.message;
      }
    }

    return error?.message || 'Ocorreu uma falha no servidor. Tente novamente mais tarde.';
  }

  private updateSettings(): void {
    if (!this.secretarySettings?.secretary_settings_id) {
      this.showErrorMessage = true;
      console.error('secretary_settings_id não encontrado para atualização.');
      return;
    }

    const updatedSecretarySettings: SecretarySettings = {
      ...this.secretarySettings,
      bank_account: this.getBankAccountPayload(),
      transfer_settings: this.getTransferSettingsPayload(),
    };

    this.loading = true;

    this.secretarySettingsService.update(updatedSecretarySettings, this.secretarySettings.secretary_settings_id).subscribe({
      next: (secretarySettings: SecretarySettings) => {
        this.showSuccessMessage = true;
        this.successSecretarySettings = true;
        this.setSecretarySettings(secretarySettings);
        this.loading = false;
        this.startOnboardingFlow();
        this.refreshSettingsAfterSave();
      },
      error: (err: any) => {
        this.loading = false;
        this.showErrorMessage = true;
        this.submittedSecretariesSettings = true;
        console.error(err);
      },
    });
  }

  private setSecretarySettings(secretarySettings: SecretarySettings): void {
    this.secretarySettings = this.mergeSecretarySettings(secretarySettings);
    this.recipient_id = this.getRecipientId(this.secretarySettings);
  }

  private getRecipientId(settings?: SecretarySettings): string {
    return settings?.recipient_info?.recipient_id || settings?.recipient_info?.id || '';
  }

  private mergeSecretarySettings(nextSettings: SecretarySettings): SecretarySettings {
    if (!this.secretarySettings) {
      return nextSettings;
    }

    return {
      ...nextSettings,
      recipient_info: this.getRecipientId(nextSettings) ? nextSettings.recipient_info : this.secretarySettings.recipient_info,
      onboard_qrcode: nextSettings.onboard_qrcode?.url ? nextSettings.onboard_qrcode : this.secretarySettings.onboard_qrcode,
    };
  }

  private refreshSettingsAfterSave(): void {
    if (this.destroyed || !this.shouldRefreshSettings()) {
      return;
    }

    if (this.settingsRefreshTimer) {
      clearTimeout(this.settingsRefreshTimer);
    }

    this.settingsRefreshTimer = setTimeout(() => {
      this.getSetting(() => {
        this.handleOnboardingStatus();
        this.openOnboardingWhenAvailable();
        this.refreshSettingsAfterSave();
      });
    }, this.settingsRefreshDelayMs);
  }

  private shouldRefreshSettings(): boolean {
    return (
      !!this.onboardingDialogRef ||
      (!!this.currentRecipientId &&
        this.recipientStatus.code !== 'active' &&
        this.recipientStatus.code !== 'inactive' &&
        !this.hasValidOnboardingLink() &&
        !this.canCreateOnboardingLink())
    );
  }
}
